import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Loader2, Plus, Pencil, Trash2, Target, Lock,
  List, BarChart2, LayoutGrid, TrendingUp, TrendingDown,
  FileText, FileSpreadsheet, RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { generateForecastPDF, generateForecastExcel } from '../utils/reportGenerators';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const MONTH_LABELS = ['', ...MONTHS];
const CUR_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CUR_YEAR - 2 + i);

const fmtEur = (n) =>
  n != null && n !== 0
    ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)
    : '—';
const fmtEurFull = (n) =>
  n != null
    ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
    : '—';
const fmtQ = (n) =>
  n != null && n !== 0 ? Number(n).toLocaleString('it-IT', { maximumFractionDigits: 3 }) : '';

// ── Module Locked ───────────────────────────────────────────────────────────────
function ModuleLocked({ notPermitted }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-5 text-center">
      <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
        <Lock size={28} className="text-amber-600 dark:text-amber-400" />
      </div>
      <div>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {notPermitted ? 'Accesso non consentito' : 'Modulo non attivo'}
        </h2>
        <p className="text-muted-foreground mt-1 max-w-md text-sm">
          {notPermitted
            ? 'Non hai i permessi per accedere al modulo Forecast. Contatta il tuo amministratore.'
            : 'Il modulo Forecast non è attivo per la tua azienda. Contatta il Super Admin per abilitarlo.'}
        </p>
      </div>
    </div>
  );
}

// ── ISO Week helpers ────────────────────────────────────────────────────────────
function getISOWeek(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const jan1 = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - jan1) / 86400000) + 1) / 7);
}

function getWeekRange(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay() || 7;
  const mon = new Date(d); mon.setDate(d.getDate() - dow + 1);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const fmt = dt => dt.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  return `${fmt(mon)} — ${fmt(sun)}`;
}

// Return the ISO date of Monday of week W in year Y
function weekToDate(year, week) {
  const jan4 = new Date(year, 0, 4);
  const dow = jan4.getDay() || 7;
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate() - dow + 1 + (week - 1) * 7);
  return mon.toISOString().slice(0, 10);
}

// ── Entry Form Dialog ───────────────────────────────────────────────────────────
function EntryForm({ open, onClose, onSave, companies, products, users, initial, currentUserId, isAdmin, saving }) {
  const blank = {
    period_type: 'monthly', period_year: CUR_YEAR,
    period_month: new Date().getMonth() + 1,
    period_week: getISOWeek(new Date().toISOString().slice(0, 10)),
    period_week_date: new Date().toISOString().slice(0, 10),
    period_date: new Date().toISOString().slice(0, 10),
    company_id: '', company_name: '', product_id: '', product_name: '',
    assigned_to: currentUserId || '', assigned_to_name: '',
    quantity: '', unit_price: '', amount: '', notes: '',
  };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (initial) {
      const weekDate = initial.period_week
        ? weekToDate(initial.period_year || CUR_YEAR, initial.period_week)
        : blank.period_week_date;
      setForm({
        ...blank,
        ...initial,
        period_week_date: weekDate,
        quantity: initial.quantity || '',
        unit_price: initial.unit_price || '',
        amount: initial.amount || '',
      });
    } else {
      setForm({ ...blank, assigned_to: currentUserId || '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = (k, v) => setForm(f => {
    const n = { ...f, [k]: v };
    if (k === 'company_id') {
      const co = companies.find(c => (c.id || c._id) === v);
      n.company_name = co?.ragione_sociale || co?.name || '';
    }
    if (k === 'product_id') {
      const pr = products.find(p => (p.id || p._id) === v);
      n.product_name = pr?.description || pr?.product_code || '';
    }
    if (k === 'assigned_to') {
      const u = users.find(u => (u.id || u._id) === v);
      n.assigned_to_name = u?.name || '';
    }
    if (k === 'period_week_date' && v) {
      n.period_week = getISOWeek(v);
      n.period_year = new Date(v + 'T00:00:00').getFullYear();
    }
    if (k === 'quantity' || k === 'unit_price') {
      const q = parseFloat(k === 'quantity' ? v : n.quantity) || 0;
      const p = parseFloat(k === 'unit_price' ? v : n.unit_price) || 0;
      if (q && p) n.amount = (q * p).toFixed(2);
    }
    return n;
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.company_id || !form.product_id) { toast.error('Seleziona azienda e prodotto'); return; }
    onSave({
      ...form,
      period_year:  parseInt(form.period_year),
      period_month: form.period_type === 'monthly' ? parseInt(form.period_month) : undefined,
      period_week:  form.period_type === 'weekly'  ? parseInt(form.period_week)  : undefined,
      period_date:  form.period_type === 'daily'   ? form.period_date            : undefined,
      quantity: parseFloat(form.quantity)   || 0,
      unit_price: parseFloat(form.unit_price) || 0,
      amount:   parseFloat(form.amount)     || 0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            {initial?.id ? 'Modifica voce Forecast' : 'Nuova voce Forecast'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Tipo Periodo</Label>
              <Select value={form.period_type} onValueChange={v => set('period_type', v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensile</SelectItem>
                  <SelectItem value="annual">Annuale</SelectItem>
                  <SelectItem value="weekly">Settimanale</SelectItem>
                  <SelectItem value="daily">Giornaliero</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Anno</Label>
              <Select value={String(form.period_year)} onValueChange={v => set('period_year', v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.period_type === 'monthly' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Mese</Label>
              <Select value={String(form.period_month)} onValueChange={v => set('period_month', v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {form.period_type === 'weekly' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Settimana — seleziona un giorno qualsiasi</Label>
              <Input
                type="date"
                value={form.period_week_date}
                onChange={e => set('period_week_date', e.target.value)}
                className="h-8"
                data-testid="forecast-week-date-input"
              />
              {form.period_week_date && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <span className="font-semibold text-foreground">
                    Settimana {getISOWeek(form.period_week_date)}
                  </span>
                  <span>·</span>
                  <span>{getWeekRange(form.period_week_date)}</span>
                </p>
              )}
            </div>
          )}

          {form.period_type === 'daily' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Giorno</Label>
              <Input
                type="date"
                value={form.period_date}
                onChange={e => set('period_date', e.target.value)}
                className="h-8"
                data-testid="forecast-date-input"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Azienda *</Label>
            <Select value={form.company_id} onValueChange={v => set('company_id', v)}>
              <SelectTrigger className="h-8"><SelectValue placeholder="Seleziona azienda..." /></SelectTrigger>
              <SelectContent>
                {companies.map(c => (
                  <SelectItem key={c.id || c._id} value={c.id || c._id}>
                    {c.ragione_sociale || c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Prodotto *</Label>
            <Select value={form.product_id} onValueChange={v => set('product_id', v)}>
              <SelectTrigger className="h-8"><SelectValue placeholder="Seleziona prodotto..." /></SelectTrigger>
              <SelectContent>
                {products.map(p => (
                  <SelectItem key={p.id || p._id} value={p.id || p._id}>
                    {p.description || p.product_code || '—'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isAdmin && (
            <div className="space-y-1.5">
              <Label className="text-xs">Assegna a</Label>
              <Select value={form.assigned_to} onValueChange={v => set('assigned_to', v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id || u._id} value={u.id || u._id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Qtà</Label>
              <Input type="number" step="0.001" value={form.quantity}
                onChange={e => set('quantity', e.target.value)} className="h-8"
                data-testid="forecast-qty-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Prezzo unit. €</Label>
              <Input type="number" step="0.01" value={form.unit_price}
                onChange={e => set('unit_price', e.target.value)} className="h-8" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Importo €</Label>
              <Input type="number" step="0.01" value={form.amount}
                onChange={e => set('amount', e.target.value)} className="h-8"
                data-testid="forecast-amount-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Note</Label>
            <Input value={form.notes} onChange={e => set('notes', e.target.value)} className="h-8" />
          </div>

          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" className="flex-1" onClick={onClose}>
              Annulla
            </Button>
            <Button type="submit" size="sm" className="flex-1" disabled={saving}
              data-testid="forecast-save-btn">
              {saving && <Loader2 size={13} className="mr-1 animate-spin" />}
              {initial?.id ? 'Aggiorna' : 'Salva'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Tab 1: Forecast List ────────────────────────────────────────────────────────
function ForecastList({ year, companies, products, users, canCreate, canEdit, canDelete, currentUserId, isAdmin }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ month: '', company_id: '', assigned_to: '' });
  const [showForm, setShowForm] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year };
      if (filters.month) params.month = filters.month;
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;
      const res = await axios.get(`${API}/forecasts`, { params });
      setEntries(res.data);
    } catch { toast.error('Errore caricamento forecast'); }
    finally { setLoading(false); }
  }, [year, filters]);

  useEffect(() => { load(); }, [load]);

  const saveEntry = async (formData) => {
    setSaving(true);
    try {
      if (editEntry?.id) {
        await axios.put(`${API}/forecasts/${editEntry.id}`, {
          quantity: formData.quantity, unit_price: formData.unit_price,
          amount: formData.amount, notes: formData.notes,
          assigned_to: formData.assigned_to, assigned_to_name: formData.assigned_to_name,
        });
        toast.success('Voce aggiornata');
      } else {
        await axios.post(`${API}/forecasts`, formData);
        toast.success('Voce creata');
      }
      setShowForm(false); setEditEntry(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Eliminare questa voce forecast?')) return;
    try {
      await axios.delete(`${API}/forecasts/${id}`);
      toast.success('Voce eliminata');
      load();
    } catch { toast.error('Errore eliminazione'); }
  };

  const filt = (k, v) => setFilters(f => ({ ...f, [k]: v === 'all' ? '' : v }));

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Mese</Label>
          <Select value={filters.month || 'all'} onValueChange={v => filt('month', v)}>
            <SelectTrigger className="h-8 w-28" data-testid="list-filter-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Azienda</Label>
          <Select value={filters.company_id || 'all'} onValueChange={v => filt('company_id', v)}>
            <SelectTrigger className="h-8 w-44" data-testid="list-filter-company"><SelectValue placeholder="Tutte" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le aziende</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.id || c._id} value={c.id || c._id}>
                  {c.ragione_sociale || c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isAdmin && (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Commerciale</Label>
            <Select value={filters.assigned_to || 'all'} onValueChange={v => filt('assigned_to', v)}>
              <SelectTrigger className="h-8 w-40" data-testid="list-filter-user"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id || u._id} value={u.id || u._id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="ml-auto">
          {canCreate && (
            <Button size="sm" onClick={() => { setEditEntry(null); setShowForm(true); }}
              data-testid="add-forecast-btn">
              <Plus size={14} className="mr-1.5" />Nuova voce
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={22} /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50 text-xs font-semibold text-muted-foreground">
                    <th className="text-left px-4 py-2.5">Periodo</th>
                    <th className="text-left px-3 py-2.5">Azienda</th>
                    <th className="text-left px-3 py-2.5">Prodotto</th>
                    {isAdmin && <th className="text-left px-3 py-2.5">Commerciale</th>}
                    <th className="text-right px-3 py-2.5">Qtà</th>
                    <th className="text-right px-3 py-2.5">Importo</th>
                    <th className="text-left px-3 py-2.5">Note</th>
                    <th className="px-3 py-2.5 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(e => (
                    <tr key={e.id} className="border-b hover:bg-muted/20 transition-colors"
                      data-testid={`forecast-row-${e.id}`}>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className="text-xs font-mono">
                          {e.period_type === 'monthly' && e.period_month
                            ? `${MONTH_LABELS[e.period_month]} ${e.period_year}`
                            : e.period_type === 'weekly' && e.period_week
                            ? `Sett. ${e.period_week} / ${e.period_year}`
                            : e.period_type === 'daily' && e.period_date
                            ? new Date(e.period_date + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
                            : e.period_year}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5 font-medium">{e.company_name || '—'}</td>
                      <td className="px-3 py-2.5 text-muted-foreground text-xs">{e.product_name || '—'}</td>
                      {isAdmin && <td className="px-3 py-2.5 text-muted-foreground text-xs">{e.assigned_to_name || '—'}</td>}
                      <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtQ(e.quantity) || '—'}</td>
                      <td className="px-3 py-2.5 text-right font-bold">{fmtEurFull(e.amount)}</td>
                      <td className="px-3 py-2.5 text-muted-foreground text-xs max-w-[100px] truncate">{e.notes}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex justify-end gap-1">
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-6 w-6"
                              onClick={() => { setEditEntry(e); setShowForm(true); }}
                              data-testid={`edit-forecast-${e.id}`}>
                              <Pencil size={12} />
                            </Button>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="icon"
                              className="h-6 w-6 text-destructive/60 hover:text-destructive"
                              onClick={() => deleteEntry(e.id)}
                              data-testid={`delete-forecast-${e.id}`}>
                              <Trash2 size={12} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {entries.length === 0 && (
                    <tr>
                      <td colSpan={isAdmin ? 8 : 7}
                        className="text-center py-12 text-muted-foreground text-sm">
                        Nessuna voce per i filtri selezionati
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <EntryForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditEntry(null); }}
        onSave={saveEntry}
        companies={companies} products={products} users={users}
        initial={editEntry}
        currentUserId={currentUserId}
        isAdmin={isAdmin}
        saving={saving}
      />
    </div>
  );
}

// Convert ISO week number + year to month (1-12)
function weekToMonth(year, week) {
  const jan4 = new Date(year, 0, 4);
  const dow = jan4.getDay() || 7;
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate() - dow + 1 + (week - 1) * 7);
  return mon.getMonth() + 1;
}

// ── Tab 2: Annual Grid ──────────────────────────────────────────────────────────
function ForecastGrid({ year, selectedUser, companies, products, users, canCreate, canEdit, currentUserId }) {
  const [data, setData] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editVals, setEditVals] = useState({ qty: '', amount: '' });
  const [saving, setSaving] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newRow, setNewRow] = useState({ company_id: '', product_id: '' });
  const qtyRef = useRef(null);

  const loadGrid = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch ALL period types — aggregate weekly/daily into month columns
      const params = { year };
      if (selectedUser && selectedUser !== 'all') params.assigned_to = selectedUser;
      const res = await axios.get(`${API}/forecasts`, { params });
      const dm = {}, rm = new Map();

      res.data.forEach(e => {
        const rk = `${e.company_id}||${e.product_id}`;
        if (!dm[rk]) dm[rk] = {};

        let month = null;
        if (e.period_type === 'monthly' && e.period_month) {
          month = e.period_month;
          // Monthly entries are editable (have id)
          dm[rk][month] = { id: e.id, qty: e.quantity, amount: e.amount };
        } else if (e.period_type === 'weekly' && e.period_week) {
          month = weekToMonth(e.period_year || year, e.period_week);
          // Aggregate weekly into month (read-only: no id)
          if (!dm[rk][month]) dm[rk][month] = { aggregated: true, qty: 0, amount: 0 };
          if (dm[rk][month].aggregated) {
            dm[rk][month].qty   = (dm[rk][month].qty   || 0) + (e.quantity || 0);
            dm[rk][month].amount = (dm[rk][month].amount || 0) + (e.amount   || 0);
          }
        } else if (e.period_type === 'daily' && e.period_date) {
          month = parseInt(e.period_date.slice(5, 7));
          // Aggregate daily into month (read-only: no id)
          if (!dm[rk][month]) dm[rk][month] = { aggregated: true, qty: 0, amount: 0 };
          if (dm[rk][month].aggregated) {
            dm[rk][month].qty   = (dm[rk][month].qty   || 0) + (e.quantity || 0);
            dm[rk][month].amount = (dm[rk][month].amount || 0) + (e.amount   || 0);
          }
        }
        // annual period: contributes to summary/analysis but not to a month column

        if (!rm.has(rk)) rm.set(rk, {
          company_id: e.company_id, company_name: e.company_name,
          product_id: e.product_id, product_name: e.product_name,
        });
      });

      setData(dm);
      setRows(Array.from(rm.values()));
    } catch { toast.error('Errore caricamento griglia'); }
    finally { setLoading(false); }
  }, [year, selectedUser]);

  useEffect(() => { loadGrid(); }, [loadGrid]);

  const canEditGrid = selectedUser !== 'all' && (canEdit || canCreate);

  const startEdit = (rk, m) => {
    if (!canEditGrid) return;
    setEditingCell({ rk, m });
    const c = data[rk]?.[m] || {};
    setEditVals({ qty: c.qty != null ? String(c.qty) : '', amount: c.amount != null ? String(c.amount) : '' });
    setTimeout(() => qtyRef.current?.focus(), 30);
  };

  const commitCell = async () => {
    if (!editingCell || saving) return;
    const { rk, m } = editingCell;
    const [company_id, product_id] = rk.split('||');
    const row = rows.find(r => r.company_id === company_id && r.product_id === product_id);
    const qty = parseFloat(editVals.qty) || 0;
    const amount = parseFloat(editVals.amount) || 0;
    const prevCell = data[rk]?.[m];
    if (!qty && !amount && !prevCell?.id) { setEditingCell(null); return; }

    setSaving(true);
    try {
      const uid = selectedUser && selectedUser !== 'all' ? selectedUser : currentUserId;
      const u = users.find(u => (u.id || u._id) === uid);
      const payload = {
        period_type: 'monthly', period_year: year, period_month: m,
        company_id, company_name: row?.company_name || '',
        product_id, product_name: row?.product_name || '',
        assigned_to: uid, assigned_to_name: u?.name || '',
        quantity: qty, unit_price: qty > 0 ? amount / qty : 0, amount, notes: '',
      };
      const res = await axios.put(`${API}/forecasts/upsert`, payload);
      setData(prev => ({
        ...prev,
        [rk]: { ...(prev[rk] || {}), [m]: { id: res.data.id, qty, amount } },
      }));
    } catch { toast.error('Errore salvataggio cella'); }
    finally { setSaving(false); setEditingCell(null); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Eliminare tutte le voci per "${row.company_name} / ${row.product_name}"?`)) return;
    const rk = `${row.company_id}||${row.product_id}`;
    try {
      for (const [, cell] of Object.entries(data[rk] || {})) {
        if (cell?.id) await axios.delete(`${API}/forecasts/${cell.id}`);
      }
      setData(prev => { const n = { ...prev }; delete n[rk]; return n; });
      setRows(prev => prev.filter(r => !(r.company_id === row.company_id && r.product_id === row.product_id)));
      toast.success('Riga eliminata');
    } catch { toast.error('Errore'); }
  };

  const addRow = () => {
    if (!newRow.company_id || !newRow.product_id) { toast.error('Seleziona azienda e prodotto'); return; }
    if (rows.some(r => r.company_id === newRow.company_id && r.product_id === newRow.product_id)) {
      toast.info('Riga già presente'); return;
    }
    const co = companies.find(c => (c.id || c._id) === newRow.company_id);
    const pr = products.find(p => (p.id || p._id) === newRow.product_id);
    setRows(prev => [...prev, {
      company_id: newRow.company_id, company_name: co?.ragione_sociale || co?.name || '',
      product_id: newRow.product_id, product_name: pr?.description || pr?.product_code || '',
    }]);
    setShowAddRow(false);
    setNewRow({ company_id: '', product_id: '' });
  };

  const rowTotal = (rk) => Object.values(data[rk] || {}).reduce((s, c) => s + (c?.amount || 0), 0);
  const monthTotal = (m) => rows.reduce((s, r) => s + (data[`${r.company_id}||${r.product_id}`]?.[m]?.amount || 0), 0);
  const grandTotal = rows.reduce((s, r) => s + rowTotal(`${r.company_id}||${r.product_id}`), 0);

  if (loading) return (
    <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={22} /></div>
  );

  return (
    <div className="space-y-3">
      {selectedUser === 'all' && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-xs">
          <Target size={13} />
          Vista aggregata (tutti i commerciali) — sola lettura. Seleziona un commerciale per modificare.
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border shadow-sm">
        <table className="text-xs border-collapse w-full">
          <thead>
            <tr className="bg-muted/70 border-b">
              <th className="sticky left-0 bg-muted/70 z-10 text-left px-3 py-3 font-semibold min-w-[220px] border-r border-border">
                Azienda / Prodotto
              </th>
              {MONTHS.map((m, i) => (
                <th key={i} className="px-2 py-3 text-center font-semibold text-muted-foreground min-w-[90px]">{m}</th>
              ))}
              <th className="px-3 py-3 text-right font-semibold text-muted-foreground min-w-[100px] border-l border-border">
                Totale
              </th>
              {canEditGrid && <th className="w-8 px-1"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const rk = `${row.company_id}||${row.product_id}`;
              return (
                <tr key={rk} className={`border-b transition-colors hover:bg-muted/10 ${ri % 2 === 1 ? 'bg-muted/5' : ''}`}>
                  <td className="sticky left-0 z-10 bg-card px-3 py-2.5 min-w-[220px] border-r border-border">
                    <p className="font-medium leading-tight">{row.company_name || '—'}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{row.product_name || '—'}</p>
                  </td>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
                    const cell = data[rk]?.[m];
                    const isEd = editingCell?.rk === rk && editingCell?.m === m;
                    const isAggregated = cell?.aggregated === true;
                    return (
                      <td key={m} className="px-1 py-1 align-middle"
                        onClick={() => !isEd && !isAggregated && startEdit(rk, m)}>
                        {isEd ? (
                          <div className="flex flex-col gap-0.5"
                            onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) commitCell(); }}>
                            <input
                              ref={qtyRef} type="number" placeholder="Qtà" value={editVals.qty}
                              onChange={e => setEditVals(v => ({ ...v, qty: e.target.value }))}
                              className="w-full h-6 text-[11px] border border-primary/50 rounded px-1 bg-background text-center outline-none"
                            />
                            <input
                              type="number" placeholder="€ Importo" value={editVals.amount}
                              onChange={e => setEditVals(v => ({ ...v, amount: e.target.value }))}
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); commitCell(); }
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              className="w-full h-6 text-[11px] border border-primary/50 rounded px-1 bg-background text-center outline-none"
                            />
                          </div>
                        ) : (
                          <div className={`rounded px-1 py-1.5 min-h-[40px] flex flex-col justify-center items-center
                            ${!isAggregated && canEditGrid ? 'cursor-pointer hover:bg-primary/10 transition-colors' : ''}
                            ${isAggregated ? 'bg-amber-50 dark:bg-amber-900/20 border border-dashed border-amber-300 dark:border-amber-700' : cell?.amount ? 'bg-primary/5' : ''}`}>
                            {cell?.amount ? (
                              <>
                                <span className={`font-semibold text-[11px] ${isAggregated ? 'text-amber-700 dark:text-amber-400' : 'text-foreground'}`}>
                                  {fmtEur(cell.amount)}
                                </span>
                                {cell.qty > 0 && (
                                  <span className="text-[9px] text-muted-foreground mt-0.5">{fmtQ(cell.qty)} pz</span>
                                )}
                                {isAggregated && (
                                  <span className="text-[8px] text-amber-600 dark:text-amber-500 mt-0.5 font-medium">agg.</span>
                                )}
                              </>
                            ) : (
                              !isAggregated && canEditGrid ? <span className="text-muted-foreground/25 text-base">+</span> : null
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 text-right font-bold border-l border-border">
                    {rowTotal(rk) > 0 ? fmtEur(rowTotal(rk)) : <span className="text-muted-foreground/30">—</span>}
                  </td>
                  {canEditGrid && (
                    <td className="px-1 py-2">
                      <button onClick={() => deleteRow(row)}
                        className="text-destructive/40 hover:text-destructive transition-colors p-0.5">
                        <Trash2 size={11} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {rows.length > 0 && (
              <tr className="bg-muted/50 font-bold border-t-2 border-border">
                <td className="sticky left-0 bg-muted/70 z-10 px-3 py-2.5 border-r border-border text-sm">Totale mensile</td>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <td key={m} className="px-2 py-2.5 text-center text-[11px]">
                    {monthTotal(m) > 0
                      ? <span className="font-bold text-primary">{fmtEur(monthTotal(m))}</span>
                      : <span className="text-muted-foreground/30">—</span>}
                  </td>
                ))}
                <td className="px-3 py-2.5 text-right border-l border-border text-primary text-sm">{fmtEur(grandTotal)}</td>
                {canEditGrid && <td />}
              </tr>
            )}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <LayoutGrid size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Nessuna riga. Clicca "Aggiungi riga" per iniziare.</p>
          </div>
        )}
      </div>

      {canCreate && (
        <Button variant="outline" size="sm" onClick={() => setShowAddRow(true)}
          data-testid="add-forecast-row-btn">
          <Plus size={14} className="mr-1.5" />Aggiungi riga
        </Button>
      )}

      <Dialog open={showAddRow} onOpenChange={setShowAddRow}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Aggiungi riga alla griglia</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Azienda *</Label>
              <Select value={newRow.company_id} onValueChange={v => setNewRow(r => ({ ...r, company_id: v }))}>
                <SelectTrigger className="h-8"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.id || c._id} value={c.id || c._id}>
                      {c.ragione_sociale || c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Prodotto *</Label>
              <Select value={newRow.product_id} onValueChange={v => setNewRow(r => ({ ...r, product_id: v }))}>
                <SelectTrigger className="h-8"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id || p._id} value={p.id || p._id}>
                      {p.description || p.product_code || '—'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowAddRow(false)}>Annulla</Button>
              <Button size="sm" className="flex-1" onClick={addRow} data-testid="confirm-add-row-btn">Aggiungi</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Tab 3: Variance Analysis ────────────────────────────────────────────────────
function ForecastAnalysis({ year, companies, users, isAdmin }) {
  const [varData, setVarData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ company_id: '', assigned_to: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year };
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;
      const sumParams = { year };
      if (filters.assigned_to) sumParams.assigned_to = filters.assigned_to;
      const [vRes, sRes] = await Promise.all([
        axios.get(`${API}/forecasts/variance`, { params }),
        axios.get(`${API}/forecasts/summary`, { params: sumParams }),
      ]);
      setVarData(vRes.data);
      setSummary(sRes.data);
    } catch { toast.error('Errore caricamento analisi'); }
    finally { setLoading(false); }
  }, [year, filters]);

  useEffect(() => { load(); }, [load]);

  const filt = (k, v) => setFilters(f => ({ ...f, [k]: v === 'all' ? '' : v }));
  const chartData = summary?.months || [];
  const tf = summary?.total_forecast || 0;
  const ta = summary?.total_actual || 0;
  const tv = ta - tf;
  const tvPct = tf > 0 ? ((tv / tf) * 100).toFixed(1) : null;

  if (loading) return (
    <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={22} /></div>
  );

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Azienda</Label>
          <Select value={filters.company_id || 'all'} onValueChange={v => filt('company_id', v)}>
            <SelectTrigger className="h-8 w-48" data-testid="analysis-company-filter"><SelectValue placeholder="Tutte" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le aziende</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.id || c._id} value={c.id || c._id}>{c.ragione_sociale || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isAdmin && (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Commerciale</Label>
            <Select value={filters.assigned_to || 'all'} onValueChange={v => filt('assigned_to', v)}>
              <SelectTrigger className="h-8 w-44" data-testid="analysis-user-filter"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i commerciali</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id || u._id} value={u.id || u._id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Budget Totale', value: fmtEurFull(tf), sub: `Anno ${year}`, icon: Target },
          { label: 'Consuntivo Totale', value: fmtEurFull(ta), sub: 'Ordini effettivi', icon: BarChart2 },
          {
            label: 'Varianza', value: (tv >= 0 ? '+' : '') + fmtEurFull(tv), sub: 'Consuntivo − Budget',
            className: tv >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400',
            cardClass: tv >= 0 ? 'border-green-200 dark:border-green-800' : 'border-red-200 dark:border-red-800',
            icon: tv >= 0 ? TrendingUp : TrendingDown,
          },
          {
            label: 'Varianza %', value: tvPct != null ? `${tv >= 0 ? '+' : ''}${tvPct}%` : '—',
            sub: tf > 0 ? 'Rispetto al budget' : 'Nessun budget',
            className: tvPct !== null && parseFloat(tvPct) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400',
            icon: BarChart2,
          },
        ].map((kpi, i) => (
          <Card key={i} className={kpi.cardClass}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <p className="text-xs text-muted-foreground">{kpi.label}</p>
                {kpi.icon && <kpi.icon size={14} className="text-muted-foreground/50" />}
              </div>
              <p className={`text-xl font-bold mt-1 ${kpi.className || ''}`}>{kpi.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{kpi.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Monthly chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Forecast vs Consuntivo — {year}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} barGap={3} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.1} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false}
                tickFormatter={v => v >= 1000 ? `€${(v / 1000).toFixed(0)}k` : `€${v}`} />
              <ReTooltip formatter={(v, name) => [fmtEurFull(v), name]} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="forecast" name="Forecast (budget)" fill="#3b82f6" radius={[3, 3, 0, 0]} />
              <Bar dataKey="actual" name="Consuntivo" fill="#22c55e" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Variance table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dettaglio Varianze
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50 text-muted-foreground font-semibold">
                  <th className="text-left px-4 py-2.5">Azienda</th>
                  <th className="text-left px-3 py-2.5">Prodotto</th>
                  {isAdmin && <th className="text-left px-3 py-2.5">Commerciale</th>}
                  <th className="text-right px-3 py-2.5">Budget €</th>
                  <th className="text-right px-3 py-2.5">Consuntivo €</th>
                  <th className="text-right px-3 py-2.5">Varianza €</th>
                  <th className="text-right px-4 py-2.5">Var. %</th>
                </tr>
              </thead>
              <tbody>
                {varData.map((row, i) => {
                  const vPos = (row.variance_amount || 0) >= 0;
                  const pctPos = (row.variance_pct || 0) >= 0;
                  return (
                    <tr key={i} className="border-b hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-2.5 font-medium">{row.company_name || '—'}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{row.product_name || '—'}</td>
                      {isAdmin && <td className="px-3 py-2.5 text-muted-foreground">{row.assigned_to_name || '—'}</td>}
                      <td className="px-3 py-2.5 text-right">{fmtEurFull(row.forecast_amount)}</td>
                      <td className="px-3 py-2.5 text-right">{fmtEurFull(row.actual_amount)}</td>
                      <td className={`px-3 py-2.5 text-right font-semibold ${vPos ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                        {(row.variance_amount || 0) >= 0 ? '+' : ''}{fmtEurFull(row.variance_amount)}
                      </td>
                      <td className={`px-4 py-2.5 text-right font-bold ${row.variance_pct == null ? 'text-muted-foreground' : pctPos ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                        {row.variance_pct != null
                          ? `${row.variance_pct >= 0 ? '+' : ''}${row.variance_pct}%`
                          : <span className="font-normal text-muted-foreground/60">n/a</span>}
                      </td>
                    </tr>
                  );
                })}
                {varData.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 7 : 6} className="text-center py-12 text-muted-foreground">
                      Nessun dato per i filtri selezionati
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab 4: Forecast Report ──────────────────────────────────────────────────────
function ForecastReport({ companies, users, isAdmin }) {
  const [year, setYear]         = useState(CUR_YEAR);
  const [filters, setFilters]   = useState({ company_id: '', assigned_to: '' });
  const [fetchState, setFetchState] = useState('idle'); // idle | fetching | ready | error
  const [loading, setLoading]   = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [varianceData, setVarianceData] = useState([]);

  // Reset when params change
  useEffect(() => { setFetchState('idle'); }, [year, filters]);

  const fetchData = async () => {
    setFetchState('fetching');
    try {
      const params = { year };
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;
      const sumParams = { year };
      if (filters.assigned_to) sumParams.assigned_to = filters.assigned_to;
      const [varRes, sumRes] = await Promise.all([
        axios.get(`${API}/forecasts/variance`, { params }),
        axios.get(`${API}/forecasts/summary`, { params: sumParams }),
      ]);
      setVarianceData(varRes.data);
      setSummaryData(sumRes.data);
      setFetchState('ready');
    } catch {
      toast.error('Errore caricamento dati report');
      setFetchState('error');
    }
  };

  const handleDownload = (format) => {
    if (fetchState !== 'ready') { toast.error('Carica prima i dati'); return; }
    setLoading(true);
    try {
      const args = { summaryData, varianceData, year, isAdmin };
      if (format === 'pdf') {
        generateForecastPDF(args);
        toast.success('PDF generato con successo');
      } else {
        generateForecastExcel(args);
        toast.success('Excel generato con successo');
      }
    } catch (e) {
      console.error(e);
      toast.error('Errore nella generazione del report');
    } finally {
      setLoading(false);
    }
  };

  const filt = (k, v) => setFilters(f => ({ ...f, [k]: v === 'all' ? '' : v }));
  const isReady = fetchState === 'ready';
  const tf = summaryData?.total_forecast || 0;
  const ta = summaryData?.total_actual   || 0;
  const tv = ta - tf;
  const tvPct = tf > 0 ? ((tv / tf) * 100).toFixed(1) : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* ── LEFT: Preview ────────────────────────────────────────────────── */}
      <div className="lg:col-span-2 space-y-5">
        {/* Section description */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Contenuto del Report
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { label: 'Riepilogo KPI', desc: 'Budget, consuntivo, varianza totale anno', icon: Target, color: 'text-blue-600' },
                { label: 'Andamento Mensile', desc: 'Trend mese per mese: forecast vs ordini effettivi', icon: BarChart2, color: 'text-green-600' },
                { label: 'Dettaglio Varianze', desc: 'Varianza per azienda / prodotto / commerciale', icon: TrendingDown, color: 'text-amber-600' },
              ].map(s => (
                <div key={s.label} className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30">
                  <div className="w-8 h-8 rounded-lg bg-background flex items-center justify-center flex-shrink-0">
                    <s.icon size={15} className={s.color} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{s.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Anteprima dati (se caricati) */}
        {isReady && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Anteprima dati — Anno {year}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* KPI preview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Budget', value: fmtEurFull(tf) },
                  { label: 'Consuntivo', value: fmtEurFull(ta) },
                  { label: 'Varianza €', value: (tv >= 0 ? '+' : '') + fmtEurFull(tv), cls: tv >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400' },
                  { label: 'Varianza %', value: tvPct != null ? `${tv >= 0 ? '+' : ''}${tvPct}%` : '—', cls: tvPct != null && parseFloat(tvPct) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400' },
                ].map(k => (
                  <div key={k.label} className="p-3 rounded-lg border bg-background text-center">
                    <p className="text-xs text-muted-foreground">{k.label}</p>
                    <p className={`text-base font-bold mt-1 ${k.cls || ''}`}>{k.value}</p>
                  </div>
                ))}
              </div>
              {/* Variance table preview (top 5) */}
              {varianceData.length > 0 && (
                <div className="rounded-lg border overflow-hidden">
                  <div className="bg-muted/50 px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Varianze (prime {Math.min(varianceData.length, 5)} righe)
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left px-3 py-2">Azienda</th>
                        <th className="text-left px-3 py-2">Prodotto</th>
                        <th className="text-right px-3 py-2">Budget</th>
                        <th className="text-right px-3 py-2">Consuntivo</th>
                        <th className="text-right px-3 py-2">Var. %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {varianceData.slice(0, 5).map((r, i) => (
                        <tr key={i} className="border-b hover:bg-muted/20">
                          <td className="px-3 py-2 font-medium">{r.company_name || '—'}</td>
                          <td className="px-3 py-2 text-muted-foreground">{r.product_name || '—'}</td>
                          <td className="px-3 py-2 text-right">{fmtEurFull(r.forecast_amount)}</td>
                          <td className="px-3 py-2 text-right">{fmtEurFull(r.actual_amount)}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${(r.variance_pct || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                            {r.variance_pct != null ? `${r.variance_pct >= 0 ? '+' : ''}${r.variance_pct}%` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {varianceData.length > 5 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      ... e altre {varianceData.length - 5} righe nel report completo
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── RIGHT: Config + Download ──────────────────────────────────────── */}
      <div className="space-y-4 lg:sticky lg:top-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Configurazione Report
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Year */}
            <div>
              <Label className="text-xs text-muted-foreground block mb-2">Anno</Label>
              <div className="flex items-center gap-1">
                <button onClick={() => setYear(y => y - 1)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg border hover:bg-muted transition-colors text-muted-foreground text-sm"
                  data-testid="forecast-report-year-prev">‹</button>
                {YEARS.map(y => (
                  <button key={y} onClick={() => setYear(y)}
                    data-testid={`forecast-report-year-${y}`}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-all ${
                      y === year ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'
                    }`}>{y}</button>
                ))}
                <button onClick={() => setYear(y => y + 1)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg border hover:bg-muted transition-colors text-muted-foreground text-sm"
                  data-testid="forecast-report-year-next">›</button>
              </div>
            </div>

            {/* Filters */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground block">Filtri (opzionali)</Label>
              <Select value={filters.company_id || 'all'} onValueChange={v => filt('company_id', v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="report-filter-company">
                  <SelectValue placeholder="Tutte le aziende" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le aziende</SelectItem>
                  {companies.map(c => (
                    <SelectItem key={c.id || c._id} value={c.id || c._id}>
                      {c.ragione_sociale || c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isAdmin && (
                <Select value={filters.assigned_to || 'all'} onValueChange={v => filt('assigned_to', v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="report-filter-user">
                    <SelectValue placeholder="Tutti i commerciali" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i commerciali</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id || u._id} value={u.id || u._id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Anteprima */}
            <div className="rounded-lg bg-muted/60 p-3 text-xs space-y-1">
              <p className="font-semibold text-foreground">Anteprima report:</p>
              <p className="text-muted-foreground">
                Anno {year}
                {filters.company_id ? ` · ${companies.find(c => (c.id || c._id) === filters.company_id)?.ragione_sociale || ''}` : ''}
                {filters.assigned_to ? ` · ${users.find(u => (u.id || u._id) === filters.assigned_to)?.name || ''}` : ''}
              </p>
              <p className="text-muted-foreground pl-2">· Riepilogo KPI</p>
              <p className="text-muted-foreground pl-2">· Andamento Mensile (12 mesi)</p>
              <p className="text-muted-foreground pl-2">· Dettaglio Varianze</p>
            </div>

            {/* Buttons */}
            <div className="space-y-2 pt-1">
              <Button onClick={fetchData} variant="outline" className="w-full gap-2"
                disabled={fetchState === 'fetching'}
                data-testid="forecast-report-load-btn">
                {fetchState === 'fetching'
                  ? <><Loader2 size={14} className="animate-spin" />Caricamento...</>
                  : <><RefreshCw size={14} className={isReady ? 'text-green-600' : ''} />
                    {isReady ? '✓ Dati pronti — Ricarica' : 'Carica dati'}</>
                }
              </Button>
              <Button onClick={() => handleDownload('pdf')}
                className="w-full gap-2 bg-rose-600 hover:bg-rose-700 text-white"
                disabled={loading || !isReady}
                data-testid="forecast-report-pdf-btn">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                Scarica PDF
              </Button>
              <Button onClick={() => handleDownload('excel')}
                className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={loading || !isReady}
                data-testid="forecast-report-excel-btn">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
                Scarica Excel
              </Button>
            </div>

            {fetchState === 'idle' && (
              <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                Clicca "Carica dati" prima di scaricare
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


// ── Main Page ───────────────────────────────────────────────────────────────────
export default function ForecastPage() {
  const { user } = useAuth();
  const { canView, canCreate, canEdit, canDelete } = usePermissions();
  const [moduleEnabled, setModuleEnabled] = useState(null);
  const [year, setYear] = useState(CUR_YEAR);
  const [gridUser, setGridUser] = useState('');
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);

  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  useEffect(() => {
    axios.get(`${API}/admin/my-modules`)
      .then(res => setModuleEnabled(res.data?.forecast === true))
      .catch(() => setModuleEnabled(false));
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/companies`),
      axios.get(`${API}/products`),
      axios.get(`${API}/users`),
    ]).then(([co, pr, us]) => {
      setCompanies(co.data || []);
      setProducts(pr.data || []);
      setUsers(us.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.id) setGridUser(isAdmin ? 'all' : user.id);
  }, [user, isAdmin]);

  if (moduleEnabled === null) return (
    <div className="flex justify-center py-16">
      <Loader2 className="animate-spin text-primary" size={28} />
    </div>
  );

  if (!moduleEnabled) return <ModuleLocked />;
  if (!canView('forecast')) return <ModuleLocked notPermitted />;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Target size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Forecast &amp; Budget
            </h1>
            <p className="text-muted-foreground text-sm">Pianificazione e simulazione budget commerciale</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Anno</Label>
          <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
            <SelectTrigger className="h-8 w-24" data-testid="forecast-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="list">
        <TabsList data-testid="forecast-tabs">
          <TabsTrigger value="list" data-testid="tab-forecast-list">
            <List size={13} className="mr-1.5" />Voci
          </TabsTrigger>
          <TabsTrigger value="grid" data-testid="tab-forecast-grid">
            <LayoutGrid size={13} className="mr-1.5" />Griglia Annuale
          </TabsTrigger>
          <TabsTrigger value="analysis" data-testid="tab-forecast-analysis">
            <BarChart2 size={13} className="mr-1.5" />Analisi
          </TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-forecast-report">
            <FileText size={13} className="mr-1.5" />Report
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <ForecastList
            year={year} companies={companies} products={products} users={users}
            canCreate={canCreate('forecast')} canEdit={canEdit('forecast')} canDelete={canDelete('forecast')}
            currentUserId={user?.id} isAdmin={isAdmin}
          />
        </TabsContent>

        <TabsContent value="grid" className="mt-4">
          <div className="space-y-3">
            {isAdmin && (
              <div className="flex items-center gap-3">
                <Label className="text-xs text-muted-foreground">Commerciale</Label>
                <Select value={gridUser} onValueChange={setGridUser}>
                  <SelectTrigger className="h-8 w-52" data-testid="grid-user-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti (vista aggregata)</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id || u._id} value={u.id || u._id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <ForecastGrid
              year={year} selectedUser={gridUser}
              companies={companies} products={products} users={users}
              canCreate={canCreate('forecast')} canEdit={canEdit('forecast')}
              currentUserId={user?.id} isAdmin={isAdmin}
            />
          </div>
        </TabsContent>

        <TabsContent value="analysis" className="mt-4">
          <ForecastAnalysis year={year} companies={companies} users={users} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="report" className="mt-4">
          <ForecastReport companies={companies} users={users} isAdmin={isAdmin} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
